import React, { useContext, useEffect, useRef, useState } from 'react';
import { AppStateContext } from '../../AppState';
import { Loader } from '@fluentui/react-northstar';
import { Ticket } from '../../shared/interfaces/ticket.interface';
import {
  getCategory,
  getGroup,
  getPriority,
  getStatusValue,
  getTicketType,
  getTeam,
} from '../ticketHelper';
import { customClassNames } from '../../shared/utils/constants';
import { TicketCardSkeleton } from '../../shared/components/TicketCard/TicketCardSkeleton';
import { TicketDetailCard } from '../EditTicket/TicketDetailCard';
import { useHistory } from 'react-router-dom';
import { EmptyData } from '../../shared/components/EmptyData';
import FlashlightSVG from './../../../svg/flashlight.svg';
import { useTranslation } from 'react-i18next';
import ListFunctions from '../List/ListFunctions';
import useCustomViewsTickets from '../List/useCustomViewsTickets';
import { ListContext } from '../List/ListContextProvider';
import { CustomViewContext } from '../toolbar/CustomViewContextProvider';
import { getCustomViewDropdownFilters } from '../toolbar/CustomViewPopup/heplers/customPopupHelper';
import { forceStyleRecalc } from '../../shared/utils/forceStyleRecalc';

interface Props {
  className: string;
  idColorPurple: boolean;
  isTicketsFetching: boolean;
  setLoading: (data: boolean) => void;
  defaultFilters: FilterPropsItems[]
}

export const MobileList = ({ className, idColorPurple, setLoading, isTicketsFetching, defaultFilters }: Props) => {
  const state = useContext(AppStateContext);
  const history = useHistory();
  const { t } = useTranslation();
  const [ticketList, setTicketList] = useState([]);
  const [page, setPage] = useState(1);
  const { listStates } = useContext(ListContext);
  const [ticketCount, setTicketCount] = useState<number>(0);
  const [isAppending, setIsAppending] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const { getpaginatedTickets } = ListFunctions();
  const { customViewStates } = useContext(CustomViewContext);
  useCustomViewsTickets({ defaultFilters }); // Get tickets filtered by custom views

  const getTicketProperties = (ticket: Ticket) => {
    return {
      Status: ticket.StatusId == null ? 'No Status' : { Value: getStatusValue(ticket.StatusId, state.ticketStatus) },
      Team: ticket.TeamId == null ? '' : { FriendlyName: getTeam(ticket.TeamId, state)?.FriendlyName },
      SupportGroup: ticket.SupportGroupId == null ? '' : { Name: getGroup(ticket.SupportGroupId, state)?.Name },
      Category: ticket.CategoryId == null ? 'No Category' : getCategory(ticket.CategoryId, state.categories),
      Priority: ticket.PriorityId == null ? 'No Priority' : getPriority(ticket.PriorityId, state.priority),
      TicketType: ticket.TicketTypeId == null ? 'No Type' : getTicketType(ticket.TicketTypeId, state.ticketTypes)
    };
  };

  useEffect(() => {
    if (!isTicketsFetching) {
      fetchTicket();
    }
  }, [state.listFilter, state.filterDropdown.queryValue, isTicketsFetching])

  useEffect(() => {
    setPage(1);
    setTicketList([]);
    setTicketCount(0);
  }, [state.listFilter, state.filterDropdown.queryValue])

  useEffect(() => {
    setIsFetching(true);
  }, [state.filterDropdown.queryValue])

  useEffect(() => {
    if (page > 1) {
      fetchTicket();
    }
  }, [page]);

  useEffect(() => {
    setLoading(false);
  }, [ticketList]);

  const listWrapRef = useRef<HTMLDivElement>(null);

  /**
   * iOS Safari can insert these rows with a genuinely zero-width layout after a filter or view
   * switch, even though the DOM holds the correct elements. See forceStyleRecalc.ts for how that
   * was diagnosed on-device and why the fix has to be a document-level style invalidation rather
   * than anything scoped to the row, its wrapper, or the scroll container — every element-local
   * attempt at this was tried and measured to fail before landing here.
   *
   * A single call keyed to `ticketList` was tried first and, on an actual device, did not hold:
   * the avatar and resolved-user-name data each row depends on arrives asynchronously (IndexedDB
   * reads, visible in every trace as `[idb] users.get`) and lands in a *later* commit, well after
   * a one-shot post-paint call had already run and moved on — so that later write to the row's own
   * subtree had nothing left correcting it. Rather than guess a second time at how long to wait,
   * this observes the actual DOM for the row content still changing and reacts to that directly,
   * for as long as it keeps happening — which is also why this is scoped to the list wrapper
   * itself rather than fired from a fixed dependency array.
   *
   * Each mutation batch is coalesced into one double-rAF-scheduled call — MutationObserver batches
   * synchronous changes into a microtask on its own, and the double rAF is what guarantees that
   * call lands after a real paint rather than merely "asynchronously"; a single rAF can still land
   * before the paint it's meant to follow.
   *
   * A first version of this cancelled and restarted that double-rAF cycle on every mutation, which
   * on a device turned out to starve itself: the avatar/user-name reads this is meant to react to
   * resolve in a tight burst (visible in every trace as several `[idb] users.get` lines within
   * ~150ms of each other), each one landing as a fresh DOM mutation faster than two animation
   * frames apart — so the pending cycle kept getting cancelled before its second frame ever ran,
   * and `forceStyleRecalc` never actually executed. This schedules once and ignores further
   * mutations only until that cycle completes, which guarantees it keeps firing regardless of how
   * densely the mutations arrive, rather than being resettable by the very thing it exists to
   * react to.
   */
  useEffect(() => {
    const el = listWrapRef.current;
    if (!el) return;

    let pending = false;
    let raf1 = 0;
    let raf2 = 0;
    const scheduleRecalc = () => {
      if (pending) return;
      pending = true;
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => {
          pending = false;
          forceStyleRecalc();
        });
      });
    };

    scheduleRecalc();

    const observer = new MutationObserver(scheduleRecalc);
    observer.observe(el, { childList: true, subtree: true, attributes: true });

    return () => {
      observer.disconnect();
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [ticketList]);

  useEffect(() => {
    const _tickets = listStates.tickets.map(ticket => ({ ...ticket, ...getTicketProperties(ticket) }))
    if (page !== 1) {
      setTicketList([...ticketList, ..._tickets]);
    } else {
      setTicketList(_tickets);
    }
    setTicketCount(listStates.countTickets);
    setIsAppending(false);
    setIsFetching(listStates.isTicketsFetching);
  }, [listStates])

  

  const fetchTicket = async () => {
    const viewFilters = customViewStates.selectedCustomView?.ViewFilters;
    const FiltersDropdownValues = viewFilters
      ? getCustomViewDropdownFilters(JSON.parse(JSON.stringify(defaultFilters)), viewFilters)
      : undefined;

    let _page = page;

    if (_page == 1) {
      setLoading(true);
    }

    const tickets = getpaginatedTickets({ page: page, FiltersDropdownValues });
    tickets.finally(() => {
      setIsAppending(false);
    })
  }

  const onScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight;
    if (isNearBottom && !isAppending && ticketCount > ticketList.length) {
      setIsAppending(true);
      setPage(page + 1);
    }
  };

  return (
    <div className={`${customClassNames.tikitBoardBody} md:hidden`} onScroll={onScroll}>
      <div ref={listWrapRef} className={`${className} p-3 mt-3`}>

        {isFetching &&
          <Loader />
        }
        {!isFetching &&
          <>
            {ticketList.length > 0 ? ticketList.map(ticket => (
              ticket.Title === 'temp' ? (
                <TicketCardSkeleton key={`skeleton-${ticket.Id}`} />
              ) : (
                // Plain wrapper rather than Fluent's Layout: Layout added a single-cell
                // grid whose sole contribution was the margin, so it was removed to shrink the
                // suspect surface. Device traces after this change still show the row collapsing
                // to width:0, so Layout was not the cause - this swap stays for its own sake.
                <div key={ticket.Id} className="m-2">
                  <div onClick={() => { history.push(`/tickets/${ticket.Id}`) }} style={{ marginBottom: '0.2rem' }}>
                    <TicketDetailCard
                      isBoardMobileView={true}
                      ticketData={ticket}
                      idColorPurple={idColorPurple}
                    />
                  </div>
                </div>
              )
            )) : (
              <EmptyData
                SVGIcon={<FlashlightSVG width={200} height={200} className={'pb-2'} />}
                headerText={t('ticket.mobile-view.empty.header')}
                subheaderText={t('ticket.mobile-view.empty.sub-header')}
              />
            )}
          </>
        }
        {isAppending &&
          <Loader />
        }
      </div>
    </div>

  );
};
